import { type FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, Transition } from '@headlessui/react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link } from '@remix-run/react';

import { COMMUNITY_ROUTES } from '~/constants/community';
import type { CommunitySearchResults } from '~/interfaces/community';

import { MemberProfileLink } from '../ui/MemberProfileLink';

import type { useCommunityTrendingPosts } from '../../_hooks/useCommunitySearch';
import { hasSearchResults } from '../../_utils/communitySearch';
import { CommentResultRow } from './_components/CommentResultRow';
import { MemberResultRow } from './_components/MemberResultRow';
import { ResourceResultRow } from './_components/ResourceResultRow';
import { SearchResultGroup } from './_components/SearchResultGroup';
import { SkeletonRow } from './_components/SkeletonRow';
import { PostResultRow } from './PostResultRow';

interface DesktopSearchModalContentProps {
  isOpen: boolean;
  query: string;
  onQueryChange: (value: string) => void;
  onClose: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onViewAll: (tab: string) => void;
  results: CommunitySearchResults | undefined;
  isSearching: boolean;
  trendingPosts: ReturnType<typeof useCommunityTrendingPosts>;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

export const DesktopSearchModalContent: FC<DesktopSearchModalContentProps> = ({
  isOpen,
  query,
  onQueryChange,
  onClose,
  onKeyDown,
  onViewAll,
  results,
  isSearching,
  trendingPosts,
  inputRef,
}) => {
  const { t } = useTranslation();

  const hasQuery = query.trim().length > 0;
  const hasResults = hasSearchResults(results);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose} initialFocus={inputRef}>
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        {/* Content — fixed position, top-aligned like DreamBuilder SearchModal */}
        <div className="fixed inset-x-0 top-16 z-50 flex justify-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full md:max-w-[585px] max-h-[90vh] flex flex-col bg-white rounded-2xl border border-black/10 shadow-[0px_0px_0px_1px_rgba(0,0,0,0.02),0px_4px_8px_rgba(0,0,0,0.08),0px_12px_30px_rgba(0,0,0,0.18)] md:mx-4">
              {/* Search input */}
              <div className="flex items-center gap-2 shrink-0 py-2 pr-2 pl-3 border-b border-black/10">
                <div className="flex items-center justify-center size-8 shrink-0">
                  <MagnifyingGlassIcon className="size-4 text-black" />
                </div>
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => onQueryChange(e.target.value)}
                  onKeyDown={onKeyDown}
                  aria-label={t('community.search.placeholder')}
                  placeholder={t('community.search.placeholder')}
                  className="flex-1 outline-none ring-0 focus:outline-none focus:ring-0 bg-transparent border-none p-0 text-[15px] leading-5 tracking-[-0.01em] text-black"
                />
                <button
                  type="button"
                  onClick={onClose}
                  aria-label={t('community.search.close')}
                  className="flex items-center justify-center size-10 shrink-0"
                >
                  <XMarkIcon className="size-6 text-black opacity-50" />
                </button>
              </div>

              {/* Results — padding 16px 0, gap 16px */}
              <div className="overflow-y-auto py-4">
                {/* Skeleton loading — shown while deferred value catches up */}
                {isSearching && (
                  <div className="flex flex-col">
                    {Array.from({ length: 5 }).map((_, i) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton list
                      <SkeletonRow key={`skeleton-${i}`} />
                    ))}
                  </div>
                )}

                {/* Trending posts — shown when query is empty */}
                {!hasQuery && !isSearching && (
                  <div className="flex flex-col">
                    <div className="px-4 py-2">
                      <span className="font-semibold opacity-50 text-[11px] leading-none text-black">
                        {t('community.search.trending_posts')}
                      </span>
                    </div>
                    {trendingPosts.isLoading
                      ? Array.from({ length: 5 }).map((_, i) => (
                          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton list
                          <SkeletonRow key={`trending-skeleton-${i}`} />
                        ))
                      : trendingPosts.data?.posts.map((post) => (
                          <PostResultRow key={post.id} post={post} onClick={onClose} />
                        ))}
                  </div>
                )}

                {!isSearching && hasQuery && hasResults && results && (
                  <div className="flex flex-col gap-4">
                    {!!results.resources?.length && (
                      <SearchResultGroup
                        title={t('community.search.resources')}
                        count={results.total_counts?.resources ?? results.resources.length}
                        viewAllHref={`${COMMUNITY_ROUTES.search}?q=${encodeURIComponent(query)}&tab=resources`}
                        onViewAllClick={() => onViewAll('resources')}
                      >
                        {results.resources.slice(0, 3).map((resource) => (
                          <ResourceResultRow key={resource.id} resource={resource} onClick={onClose} />
                        ))}
                      </SearchResultGroup>
                    )}

                    {results.posts.length > 0 && (
                      <SearchResultGroup
                        title={t('community.search.posts')}
                        count={results.total_counts?.posts ?? results.posts.length}
                        viewAllHref={`${COMMUNITY_ROUTES.search}?q=${encodeURIComponent(query)}&tab=posts`}
                        onViewAllClick={() => onViewAll('posts')}
                      >
                        {results.posts.slice(0, 3).map((post) => (
                          <PostResultRow key={post.id} post={post} onClick={onClose} />
                        ))}
                      </SearchResultGroup>
                    )}

                    {results.comments.length > 0 && (
                      <SearchResultGroup
                        title={t('community.search.comments')}
                        count={results.total_counts?.comments ?? results.comments.length}
                        viewAllHref={`${COMMUNITY_ROUTES.search}?q=${encodeURIComponent(query)}&tab=comments`}
                        onViewAllClick={() => onViewAll('comments')}
                      >
                        {results.comments.slice(0, 3).map((comment) => (
                          <Link
                            key={comment.id}
                            to={`${COMMUNITY_ROUTES.root}/posts/${comment.post.id}#${comment.id}`}
                            onClick={onClose}
                          >
                            <CommentResultRow comment={comment} />
                          </Link>
                        ))}
                      </SearchResultGroup>
                    )}

                    {results.members.length > 0 && (
                      <SearchResultGroup
                        title={t('community.search.members')}
                        count={results.total_counts?.members ?? results.members.length}
                        viewAllHref={`${COMMUNITY_ROUTES.search}?q=${encodeURIComponent(query)}&tab=members`}
                        onViewAllClick={() => onViewAll('members')}
                      >
                        {results.members.slice(0, 3).map((member) => (
                          <MemberProfileLink key={member.id} memberId={member.id} onClick={onClose}>
                            <MemberResultRow member={member} />
                          </MemberProfileLink>
                        ))}
                      </SearchResultGroup>
                    )}
                  </div>
                )}

                {!isSearching && hasQuery && !hasResults && (
                  <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                    <p className="text-sm font-medium text-black/70">{t('community.search.no_results')}</p>
                    <p className="text-xs text-black/50 mt-1">{t('community.search.no_results_description')}</p>
                  </div>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};
